import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useAuthStore } from '@features/auth'
import { EmergencyNotice, useEmergencyNotice } from '@features/emergency-notice'

const DISMISSED_NOTICE_STORAGE_KEY =
  import.meta.env.VITE_PLATFORM === 'MOBILE'
    ? 'dismissedEmergencyNoticeIds:mobile'
    : 'dismissedEmergencyNoticeIds:pc'

interface EmergencyNoticeProviderProps {
  children: ReactNode
}

export const EmergencyNoticeProvider = ({ children }: EmergencyNoticeProviderProps) => {
  const isLoggedIn = useAuthStore(state => state.isLoggedIn)
  const [clientNow, setClientNow] = useState(() => Math.floor(Date.now() / 1000))
  const [closedNoticeIdx, setClosedNoticeIdx] = useState<number | null>(null)
  const [dismissedNoticeIds, setDismissedNoticeIds] = useState<Set<number>>(() =>
    readDismissedNoticeIds()
  )
  const emergencyNoticeOptions = useMemo(
    () => ({
      canShowNotice: isLoggedIn,
      dismissedNoticeIds,
    }),
    [dismissedNoticeIds, isLoggedIn]
  )
  const { notice, serverNow } = useEmergencyNotice(clientNow, emergencyNoticeOptions)

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setClientNow(Math.floor(Date.now() / 1000))
    }, 1000)

    return () => window.clearInterval(timerId)
  }, [])

  useEffect(() => {
    if (!notice) {
      setClosedNoticeIdx(null)
    }
  }, [notice])

  const showNoticePopup =
    isLoggedIn &&
    notice?.type === 'notice' &&
    notice.idx !== closedNoticeIdx &&
    !dismissedNoticeIds.has(notice.idx)

  const closeNotice = () => {
    if (!notice) return
    setClosedNoticeIdx(notice.idx)
  }

  const dismissNotice = () => {
    if (!notice) return

    setDismissedNoticeIds(prev => {
      const next = new Set(prev)
      next.add(notice.idx)
      writeDismissedNoticeIds(next)
      return next
    })
    setClosedNoticeIdx(notice.idx)
  }

  if (notice?.type === 'maintenance') {
    return <MaintenanceScreen notice={notice} now={serverNow} />
  }

  return (
    <>
      {children}
      {showNoticePopup && (
        <NoticePopup
          notice={notice}
          onClose={closeNotice}
          onDismiss={dismissNotice}
        />
      )}
    </>
  )
}

const NoticePopup = ({
  notice,
  onClose,
  onDismiss,
}: {
  notice: EmergencyNotice
  onClose: () => void
  onDismiss: () => void
}) => (
  <div
    role="dialog"
    aria-modal="true"
    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/65 px-3 py-4 sm:px-4 sm:py-6"
  >
    <section className="relative flex max-h-[calc(100vh-32px)] w-[calc(100vw-24px)] max-w-[560px] flex-col overflow-hidden rounded-[8px] border border-[#3a3d40] bg-[#191a1b] shadow-[0px_18px_48px_rgba(0,0,0,0.55)] sm:max-h-[calc(100vh-48px)] sm:w-full">
      <div className="h-1 w-full bg-[#faea4d]" aria-hidden="true" />

      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-[#8f9194] transition-colors hover:bg-[#2a2b2b] hover:text-[#efe9e9]"
        aria-label="닫기"
      >
        <span className="text-[22px] leading-none">×</span>
      </button>

      <div className="min-h-0 px-5 pb-5 pt-5 sm:px-6">
        <div className="mb-4 inline-flex h-7 items-center rounded-full border border-[#faea4d]/30 bg-[#faea4d]/10 px-3 font-pretendard text-[12px] font-semibold text-[#faea4d]">
          긴급알림
        </div>

        <HtmlContent
          html={notice.title || '긴급알림'}
          className="pr-9 font-pretendard text-[20px] font-semibold leading-7 text-[#efe9e9]"
        />

        <div className="mt-5 rounded-[8px] border border-[#32363d] bg-[#202121] px-4 py-4">
          <HtmlContent
            html={notice.message}
            className="max-h-[52vh] overflow-y-auto whitespace-pre-line pr-1 font-pretendard text-[14px] leading-7 text-[#d7d2d4] sm:max-h-[460px]"
          />
        </div>
      </div>

      <div className="flex shrink-0 gap-2 border-t border-[#32363d] bg-[#171819] px-5 py-4 sm:px-6">
        <button
          type="button"
          onClick={onDismiss}
          className="flex h-11 flex-1 items-center justify-center rounded-full border border-[#444546] bg-transparent font-pretendard text-[13px] font-semibold text-[#efe9e9] transition-colors hover:bg-[#262728]"
        >
          다시 보지 않기
        </button>
        <button
          type="button"
          onClick={onClose}
          className="flex h-11 flex-1 items-center justify-center rounded-full bg-[#efe9e9] font-pretendard text-[13px] font-semibold text-[#1b1c1d] transition-colors hover:bg-white"
        >
          확인
        </button>
      </div>
    </section>
  </div>
)

const MaintenanceScreen = ({
  notice,
  now,
}: {
  notice: EmergencyNotice
  now: number
}) => {
  const remainingSeconds = Math.max(notice.endAt - now, 0)

  return (
    <main className="flex min-h-screen items-center justify-center bg-white px-5 py-8 text-center text-[#111111]">
      <section
        role="alertdialog"
        aria-modal="true"
        className="w-full max-w-[440px] rounded-[8px] border border-[#dedede] bg-white px-6 py-7 shadow-[0_16px_50px_rgba(0,0,0,0.12)]"
      >
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-[#fff2ba] text-[22px] font-bold text-[#725500]">
          !
        </div>
        <p className="font-pretendard text-[28px] font-semibold leading-9">
          점검중입니다
        </p>
        {notice.title && (
          <HtmlContent
            html={notice.title}
            className="mt-4 font-pretendard text-[18px] font-semibold leading-7 text-[#222222]"
            tone="light"
          />
        )}
        <HtmlContent
          html={notice.message}
          className="mt-3 whitespace-pre-line font-pretendard text-[15px] leading-7 text-[#555555]"
          tone="light"
        />
        <div className="mt-7 rounded-[8px] border border-[#d8d8d8] bg-[#f8f8f8] px-5 py-4">
          <p className="font-pretendard text-[12px] font-semibold text-[#777777]">
            점검 종료까지
          </p>
          <p className="mt-2 font-pretendard text-[34px] font-semibold tabular-nums leading-10 text-[#111111]">
            {formatCountdown(remainingSeconds)}
          </p>
        </div>
        <p className="mt-5 font-pretendard text-[13px] text-[#777777]">
          종료 예정: {formatDateTime(notice.endAt)}
        </p>
      </section>
    </main>
  )
}

const HtmlContent = ({
  html,
  className,
  tone = 'dark',
}: {
  html: string
  className: string
  tone?: 'dark' | 'light'
}) => (
  <div
    className={`${className} [&_a]:underline [&_em]:italic [&_li+li]:mt-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_p+p]:mt-2 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 ${tone === 'dark' ? '[&_a]:text-[#faea4d] [&_strong]:text-[#efe9e9]' : '[&_a]:text-[#1f65d6] [&_strong]:text-[#111111]'}`}
    dangerouslySetInnerHTML={{ __html: html }}
  />
)

const formatCountdown = (totalSeconds: number) => {
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const time = [hours, minutes, seconds]
    .map(value => String(value).padStart(2, '0'))
    .join(':')

  return days > 0 ? `${days}일 ${time}` : time
}

const formatDateTime = (timestamp: number) =>
  new Date(timestamp * 1000).toLocaleString()

const readDismissedNoticeIds = () => {
  if (typeof window === 'undefined') return new Set<number>()

  try {
    const raw = window.localStorage.getItem(DISMISSED_NOTICE_STORAGE_KEY)
    const values = raw ? JSON.parse(raw) : []
    if (!Array.isArray(values)) return new Set<number>()
    return new Set(values.filter((value): value is number => typeof value === 'number'))
  } catch {
    return new Set<number>()
  }
}

const writeDismissedNoticeIds = (ids: Set<number>) => {
  window.localStorage.setItem(DISMISSED_NOTICE_STORAGE_KEY, JSON.stringify(Array.from(ids)))
}
